import type { Angle } from '@geom';
import {
  clamp,
  fixedNumber,
  isClose,
  lerp,
  toHexFloat,
  toPercentFloat,
  type Interpable,
  type Lerpable
} from '@utils';
import { z } from 'zod';
import type { ColorMode } from './ColorMode';
import type { IColor } from './IColor';

/*

# `Color` At A Glance

`Color` represents an RGBA color and provides a variety of functionalities for color manipulation and interpolation. It:

- **Immutable**: Instances cannot be modified after creation.
- **Properties**:
  - `red`: The red component (0 to 1).
  - `green`: The green component (0 to 1).
  - `blue`: The blue component (0 to 1).
  - `alpha`: The alpha (opacity) component (0 to 1).
- **Constructor**:
  - Accepts individual `red`, `green`, `blue`, and optional `alpha` values.
  - Accepts an array of 3 or 4 numbers representing `[red, green, blue, alpha?]`.
  - Accepts an object with `red`, `green`, `blue`, and optional `alpha` properties.
  - Accepts a `Float32Array` with 3 or 4 elements.
  - Defaults to a fully opaque color if no arguments are provided.
- **Static Constants**: Predefined common colors such as `BLACK`, `WHITE`, `RED`, `GREEN`, `BLUE`, `YELLOW`, `CYAN`, `MAGENTA`, `ORANGE`, `PURPLE`, `VIOLET`, and `TRANSPARENT`.
- **Methods**:
  - **Interpolation**:
    - `lerp(t: number, end: Color)`: Linearly interpolates between this color and another.
    - `interp(end: Color)`: Returns a function to interpolate between this color and another.
  - **Color Manipulation**:
    - `lighten(t: number)`: Lightens the color by a specified amount.
    - `darken(t: number)`: Darkens the color by a specified amount.
    - `withOpacity(alpha: number)`: Returns a new color with the specified alpha value.
  - **Comparison**:
    - `equals(other: Color | ColorObject)`: Checks if another color is equal to this one.
  - **Clamping**:
    - `clamped()`: Returns a new color with all components clamped to the [0, 1] range.
  - **Conversion**:
    - `toCSS(fractionDigits?: number)`: Converts the color to a CSS `rgba()` string.
    - `toCSSHex()`: Converts the color to a CSS hexadecimal string.
    - `toCSSHexAlpha()`: Converts the color to a CSS hexadecimal string with alpha.
    - `toJSON()`: Serializes the color to JSON.
    - `fromJSON(json: unknown)`: Deserializes a color from JSON.
    - `toString(fractionDigits?: number)`: Returns a string representation of the color.
    - `typedArray`: Returns the color as a `Float32Array`.
    - `ckColor`: Returns the color in a format compatible with CanvasKit.
- **Properties**:
  - `luminance`: Calculates the luminance of the color.
  - `isLightColor`: Determines if the color is considered light based on luminance.
  - `isTransparent`: Checks if the color is fully transparent.
  - `textColor`: Suggests an appropriate text color (black or white) based on the background color's luminance.
- **Utility Functions**:
  - `from8Bit(r: number, g: number, b: number)`: Creates a color from 8-bit RGB values.
  - `fromHsl(hue: Angle, saturation: number, lightness: number, alpha?: number)`: Creates a color from HSL values.
  - `lerpOptionalColor(t: number, range: [Color?, Color?])`: Interpolates between two optional colors.
  - `interpOptionalColor(range: [Color?, Color?])`: Returns an interpolation function for two optional colors.

*/

export interface ColorObject {
  red: number;
  green: number;
  blue: number;
  alpha: number;
}

export class Color implements ColorObject, Lerpable, Interpable, IColor {
  private readonly _red: number;
  private readonly _green: number;
  private readonly _blue: number;
  private readonly _alpha: number;

  get red(): number {
    return this._red;
  }
  get green(): number {
    return this._green;
  }
  get blue(): number {
    return this._blue;
  }
  get alpha(): number {
    return this._alpha;
  }

  constructor(
    red:
      | number
      | [number, number, number, number?]
      | { red: number; green: number; blue: number; alpha?: number }
      | Float32Array = 0,
    green?: number,
    blue?: number,
    alpha?: number
  ) {
    if (
      typeof red === 'number' &&
      typeof green === 'number' &&
      typeof blue === 'number' &&
      (alpha === undefined || typeof alpha === 'number')
    ) {
      // red, green, blue, and alpha are numbers
      this._red = red;
      this._green = green;
      this._blue = blue;
      this._alpha = alpha ?? 1;
    } else if (Array.isArray(red)) {
      // red is an array of numbers of length 3 or 4
      // if (red.length === 3) {
      this._red = red[0];
      this._green = red[1];
      this._blue = red[2];
      this._alpha = red[3] ?? 1;
      // } else {
      // throw new Error('Invalid arguments');
      // }
    } else if (red instanceof Float32Array && (red.length === 3 || red.length === 4)) {
      // red is a Float32Array of length 3 or 4
      this._red = red[0];
      this._green = red[1];
      this._blue = red[2];
      this._alpha = red.length > 3 ? red[3] : 1;
    } else if (
      typeof red === 'object' &&
      'red' in red &&
      'green' in red &&
      'blue' in red
    ) {
      // red is an object with red, green, and blue properties, and optionally an alpha property
      this._red = red.red;
      this._green = red.green;
      this._blue = red.blue;
      this._alpha = red.alpha ?? 1;
    } else if (typeof red === 'number') {
      // red is a single number (default value of 0) and green, blue, and alpha are undefined
      this._red = red;
      this._green = green ?? red;
      this._blue = blue ?? red;
      this._alpha = alpha ?? 1;
    } else {
      throw new Error('Invalid arguments');
    }
  }

  static from8Bit(r: number, g: number, b: number, a?: number): Color {
    return new Color(r / 255, g / 255, b / 255, a);
  }

  /**
   * Creates a Color instance from HSL (Hue, Saturation, Lightness) values.
   * @param hue - The hue component as an angle.
   * @param saturation - The saturation component (0 - 1).
   * @param lightness - The lightness component (0 - 1).
   * @param alpha - The alpha component (0 - 1), optional, defaults to 1.
   * @returns A new Color instance.
   */
  static fromHsl(
    hue: Angle,
    saturation: number,
    lightness: number,
    alpha?: number
  ): Color {
    const h = hue.normalized.t;

    const s = saturation;
    const l = lightness;
    let r: number, g: number, b: number;

    if (s === 0) {
      // Achromatic color (gray scale)
      r = g = b = l;
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = Color.hue2rgb(p, q, h + 1 / 3);
      g = Color.hue2rgb(p, q, h);
      b = Color.hue2rgb(p, q, h - 1 / 3);
    }

    return new Color(r, g, b, alpha ?? 1);
  }

  /**
   * Helper function for HSL to RGB conversion.
   * @param p - Temporary value used in calculation.
   * @param q - Temporary value used in calculation.
   * @param t - Temporary hue value.
   * @returns The RGB component (0 - 1).
   */
  private static hue2rgb(p: number, q: number, t: number): number {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }

  get light(): this {
    return this;
  }

  get dark(): this {
    return this;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  realize(mode: ColorMode): this {
    return this;
  }

  static BLACK = new Color(0, 0, 0);
  static WHITE = new Color(1, 1, 1);
  static GRAY = new Color(0.5, 0.5, 0.5);
  static RED = new Color(1, 0, 0);
  static GREEN = new Color(0, 1, 0);
  static BLUE = new Color(0, 0, 1);
  static YELLOW = new Color(1, 1, 0);
  static CYAN = new Color(0, 1, 1);
  static MAGENTA = new Color(1, 0, 1);
  static ORANGE = new Color(1, 0.5, 0);
  static PURPLE = new Color(0.5, 0, 0.5);
  static VIOLET = new Color(0.5, 0, 1);
  static TRANSPARENT = new Color(0, 0, 0, 0);

  /**
   * Returns whether this color is equal to another color.
   */
  equals(other: Color | ColorObject): boolean {
    if (this === other) return true;
    return (
      this.red === other.red &&
      this.green === other.green &&
      this.blue === other.blue &&
      this.alpha === other.alpha
    );
  }

  /**
   * Returns a new color with each component clamped to the range [0, 1].
   */
  clamped(): Color {
    return new Color(
      clamp(this.red),
      clamp(this.green),
      clamp(this.blue),
      clamp(this.alpha)
    );
  }

  /**
   * Returns a string representation of the color in CSS rgba() format.
   */
  toCSS(fractionDigits?: number): string {
    const digits = fractionDigits ?? 1;
    if (this.alpha < 1) {
      return `rgba(${toPercentFloat(this.red, digits)}, ${toPercentFloat(this.green, digits)}, ${toPercentFloat(this.blue, digits)}, ${fixedNumber(this.alpha, digits)})`;
    } else {
      return `rgb(${toPercentFloat(this.red, digits)}, ${toPercentFloat(this.green, digits)}, ${toPercentFloat(this.blue, digits)})`;
    }
  }

  toHex(): string {
    if (this.alpha < 1) {
      return this.toHexRGBA();
    } else {
      return this.toHexRGB();
    }
  }

  /**
   * Returns the name of the closest named color if this color is close to one,
   * otherwise returns undefined. Uses O(1) lookup for performance.
   */
  private getClosestColorName(): string | undefined {
    // First check exact match for O(1) performance
    const exactKey = createColorKey(this);
    const exactMatch = COLOR_LOOKUP.get(exactKey);
    if (exactMatch) {
      return exactMatch;
    }

    // Check if close to any named color using epsilon comparison
    for (const [name, namedColor] of Object.entries(COLORS_BY_NAME)) {
      if (this.isCloseTo(namedColor)) {
        return name;
      }
    }

    return undefined;
  }

  /**
   * Checks if this color is close to another color within epsilon tolerance.
   */
  private isCloseTo(other: Color): boolean {
    return (
      isClose(this.red, other.red) &&
      isClose(this.green, other.green) &&
      isClose(this.blue, other.blue) &&
      isClose(this.alpha, other.alpha)
    );
  }

  /**
   * Returns a string representation of the color in CSS hex format.
   */
  toHexRGB(): string {
    return `#${toHexFloat(this.red)}${toHexFloat(this.green)}${toHexFloat(this.blue)}`;
  }

  /**
   * Returns a string representation of the color in CSS hex format with alpha.
   */
  toHexRGBA(): string {
    return `#${toHexFloat(this.red)}${toHexFloat(this.green)}${toHexFloat(this.blue)}${toHexFloat(this.alpha)}`;
  }

  /**
   * Interpolates between two colors.
   */
  lerp(t: number, end: this): this {
    return new Color(
      lerp(t, [this.red, end.red]),
      lerp(t, [this.green, end.green]),
      lerp(t, [this.blue, end.blue]),
      lerp(t, [this.alpha, end.alpha])
    ) as this;
  }

  /**
   * Returns a function that interpolates between this color and another color.
   */
  interp(end: this): (t: number) => this {
    return (t: number) => this.lerp(t, end);
  }

  /**
   * Lightens the color by the given amount.
   */
  lighten(t: number): this {
    return this.lerp(t, Color.WHITE as this);
  }

  /**
   * Darkens the color by the given amount.
   */
  darken(t: number): this {
    return this.lerp(t, Color.BLACK as this);
  }

  /**
   * Returns a new color with the given alpha value.
   */
  withOpacity(alpha: number): Color {
    return new Color(this.red, this.green, this.blue, alpha);
  }

  private static srgbToLinear(c: number): number {
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }

  /**
   * Relative luminance (Y) per ITU-R BT.709 / sRGB, with the RGB components
   * converted to linear light first.
   *
   * Why linearise first? The stored red, green, and blue fields are
   * sRGB-encoded (γ-compressed). The BT.709 coefficients only model human
   * brightness perception in linear light; skipping the conversion would bias
   * the result toward the mid-tones and mis-pick the text colour for most hues.
   */
  get luminance(): number {
    const R = Color.srgbToLinear(this.red);
    const G = Color.srgbToLinear(this.green);
    const B = Color.srgbToLinear(this.blue);
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
  }

  /**
   * True for backgrounds lighter than the WCAG black/white contrast crossover
   * (Y ≈ 0.179).
   *
   * Why 0.179? It’s the exact luminance where the WCAG contrast ratios to pure
   * black and pure white are equal (≈ 4.5 : 1).
   */
  get isLightColor(): boolean {
    return this.luminance > 0.179;
  }

  /**
   * Black text on light backgrounds, white on dark, maximizing contrast by the
   * WCAG criterion.
   */
  get textColor(): Color {
    return this.isLightColor ? Color.BLACK : Color.WHITE;
  }

  /**
   * Returns true if this color is transparent (alpha = 0).
   */
  get isTransparent(): boolean {
    return this.alpha === 0;
  }

  //
  // Type conversion
  //

  /*
   * Returns the color as a Float32Array.
   */
  get typedArray(): Float32Array {
    return new Float32Array([this.red, this.green, this.blue, this.alpha]);
  }

  /**
   * Returns the color as a CanvasKit color.
   */
  get ckColor(): Float32Array {
    return this.typedArray;
  }

  //
  // Serialization
  //

  toJSON(): unknown {
    // Check if this color is close to any named color
    const colorName = this.getClosestColorName();
    if (colorName) {
      return colorName;
    }

    return this.toHex();
  }

  static fromJSON(json: unknown): Color {
    if (!Array.isArray(json) || json.length < 3 || json.length > 4) {
      throw new Error('Invalid Color JSON, expected array of length 3 or 4');
    }
    if (!json.every(x => typeof x === 'number')) {
      throw new Error('Invalid Color JSON, expected array of numbers');
    }
    const [r, g, b, a] = json;
    return new Color(r, g, b, a);
  }

  //
  // String conversion
  //

  /**
   * Formats the `Color` as a string for debugging.
   */
  toString(fractionDigits?: number): string {
    return `{r: ${fixedNumber(this.red, fractionDigits)}, b: ${fixedNumber(this.green, fractionDigits)}, g: ${fixedNumber(this.blue, fractionDigits)}, a: ${fixedNumber(this.alpha, fractionDigits)}}`;
  }
}

/**
 * Interpolates between two colors, but allows either color to be undefined.
 *
 * If both colors are undefined, returns transparent.
 * If one color is undefined, interpolates between the other color and transparent.
 *
 * @param t The interpolation parameter.
 * @param range The range of colors to interpolate between.
 * @returns
 */
export function lerpOptionalColor(t: number, range: [Color?, Color?]): Color {
  const [a, b] = range;
  if (a === undefined) {
    if (b === undefined) {
      return Color.TRANSPARENT;
    } else {
      return b;
    }
  } else {
    if (b === undefined) {
      return a;
    } else {
      return a.lerp(t, b);
    }
  }
}

/**
 * Returns a function that interpolates between two colors, but allows either color to be undefined.
 *
 * If both colors are undefined, returns transparent.
 * If one color is undefined, interpolates between the other color and transparent.
 *
 * @param a The first color.
 * @param b The second color.
 * @returns The interpolation function.
 */
export function interpOptionalColor(range: [Color?, Color?]): (t: number) => Color {
  const [a, b] = range;
  if (a === undefined) {
    if (b === undefined) {
      return () => Color.TRANSPARENT;
    } else {
      return Color.TRANSPARENT.interp(b);
    }
  } else {
    if (b === undefined) {
      return a.interp(Color.TRANSPARENT);
    } else {
      return a.interp(b);
    }
  }
}

// Helper function to parse hex colors
function parseHex(hex: string): Color {
  // Remove # if present
  const cleanHex = hex.replace(/^#/, '');

  if (cleanHex.length === 3) {
    // Short hex format: #RGB -> #RRGGBB
    const r = parseInt(cleanHex[0] + cleanHex[0], 16) / 255;
    const g = parseInt(cleanHex[1] + cleanHex[1], 16) / 255;
    const b = parseInt(cleanHex[2] + cleanHex[2], 16) / 255;
    return new Color(r, g, b);
  } else if (cleanHex.length === 6) {
    // Standard hex format: #RRGGBB
    const r = parseInt(cleanHex.slice(0, 2), 16) / 255;
    const g = parseInt(cleanHex.slice(2, 4), 16) / 255;
    const b = parseInt(cleanHex.slice(4, 6), 16) / 255;
    return new Color(r, g, b);
  } else if (cleanHex.length === 8) {
    // Hex with alpha: #RRGGBBAA
    const r = parseInt(cleanHex.slice(0, 2), 16) / 255;
    const g = parseInt(cleanHex.slice(2, 4), 16) / 255;
    const b = parseInt(cleanHex.slice(4, 6), 16) / 255;
    const a = parseInt(cleanHex.slice(6, 8), 16) / 255;
    return new Color(r, g, b, a);
  }

  throw new Error(`Invalid hex color format: ${hex}`);
}

// Helper function to parse rgb/rgba functions
function parseRgb(rgbString: string): Color {
  const regex = /rgba?\(\s*([^)]+)\s*\)/;
  const match = regex.exec(rgbString);
  if (!match) {
    throw new Error(`Invalid rgb/rgba format: ${rgbString}`);
  }

  const values = match[1].split(',').map(v => v.trim());

  if (values.length < 3 || values.length > 4) {
    throw new Error(`Invalid rgb/rgba format: ${rgbString}`);
  }

  const r = parseFloat(values[0]);
  const g = parseFloat(values[1]);
  const b = parseFloat(values[2]);
  const a = values.length === 4 ? parseFloat(values[3]) : 1;

  // Validate that all values are valid numbers
  if (isNaN(r) || isNaN(g) || isNaN(b) || (values.length === 4 && isNaN(a))) {
    throw new Error(`Invalid rgb/rgba format: ${rgbString}`);
  }

  return new Color(r / 255, g / 255, b / 255, a);
}

// Helper function to parse hsl/hsla functions
function parseHsl(hslString: string): Color {
  const regex = /hsla?\(\s*([^)]+)\s*\)/;
  const match = regex.exec(hslString);
  if (!match) {
    throw new Error(`Invalid hsl/hsla format: ${hslString}`);
  }

  const values = match[1].split(',').map(v => v.trim());

  if (values.length < 3 || values.length > 4) {
    throw new Error(`Invalid hsl/hsla format: ${hslString}`);
  }

  const h = parseFloat(values[0]) % 360; // Hue in degrees
  const s = parseFloat(values[1].replace('%', '')) / 100; // Saturation 0-1
  const l = parseFloat(values[2].replace('%', '')) / 100; // Lightness 0-1
  const a = values.length === 4 ? parseFloat(values[3]) : 1;

  // Validate that all values are valid numbers
  if (isNaN(h) || isNaN(s) || isNaN(l) || (values.length === 4 && isNaN(a))) {
    throw new Error(`Invalid hsl/hsla format: ${hslString}`);
  }

  // Convert HSL to RGB
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r: number, g: number, b: number;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  return new Color(r + m, g + m, b + m, a);
}

// Color schema that handles all basic color formats and produces a Color object
export const BasicColorSchema = z.union([
  // Array format: [r, g, b] or [r, g, b, a]
  z
    .array(z.number())
    .min(3)
    .max(4)
    .transform(arr => {
      const [r, g, b, a] = arr;
      return new Color(r, g, b, a);
    }),
  // Hex format: #RGB, #RRGGBB, #RRGGBBAA (with or without #)
  z
    .string()
    .regex(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/)
    .transform(parseHex),
  // RGB/RGBA format: rgb(r, g, b) or rgba(r, g, b, a)
  z
    .string()
    .regex(/^rgba?\(\s*[^)]+\s*\)$/)
    .transform(parseRgb),
  // HSL/HSLA format: hsl(h, s%, l%) or hsla(h, s%, l%, a)
  z
    .string()
    .regex(/^hsla?\(\s*[^)]+\s*\)$/)
    .transform(parseHsl)
]);

export const COLOR_BLACK = 'black';
export const COLOR_WHITE = 'white';
export const COLOR_GRAY = 'gray';
export const COLOR_RED = 'red';
export const COLOR_GREEN = 'green';
export const COLOR_BLUE = 'blue';
export const COLOR_YELLOW = 'yellow';
export const COLOR_CYAN = 'cyan';
export const COLOR_MAGENTA = 'magenta';
export const COLOR_ORANGE = 'orange';
export const COLOR_PURPLE = 'purple';
export const COLOR_VIOLET = 'violet';
export const COLOR_TRANSPARENT = 'transparent';

// Map of Color static constants by name
export const COLORS_BY_NAME = {
  [COLOR_BLACK]: Color.BLACK,
  [COLOR_WHITE]: Color.WHITE,
  [COLOR_GRAY]: Color.GRAY,
  [COLOR_RED]: Color.RED,
  [COLOR_GREEN]: Color.GREEN,
  [COLOR_BLUE]: Color.BLUE,
  [COLOR_YELLOW]: Color.YELLOW,
  [COLOR_CYAN]: Color.CYAN,
  [COLOR_MAGENTA]: Color.MAGENTA,
  [COLOR_ORANGE]: Color.ORANGE,
  [COLOR_PURPLE]: Color.PURPLE,
  [COLOR_VIOLET]: Color.VIOLET,
  [COLOR_TRANSPARENT]: Color.TRANSPARENT
} as const;

export const COLOR_NAMES = Object.keys(COLORS_BY_NAME);

// Reverse lookup map for O(1) color name resolution
// Maps color key (r,g,b,a values as string) to color name
const COLOR_LOOKUP = new Map<string, string>();

// Helper function to create a lookup key for a color
function createColorKey(color: Color): string {
  return `${color.red},${color.green},${color.blue},${color.alpha}`;
}

// Initialize the reverse lookup map
for (const [name, color] of Object.entries(COLORS_BY_NAME)) {
  COLOR_LOOKUP.set(createColorKey(color), name);
}

// Schema for named basic colors only
export const NamedBasicColorSchema = z.string().transform((name, ctx): IColor => {
  // Check for static Color constants
  if (name in COLORS_BY_NAME) {
    return COLORS_BY_NAME[name as keyof typeof COLORS_BY_NAME];
  }

  // Add validation issue instead of throwing
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: `Unknown basic color name: ${name}`
  });
  return z.NEVER;
});
